import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  CalendarMonth,
  CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';
import { extractArrayFromResponse } from '../utils/apiHelpers';
import { Modal } from './common/Modal';

interface Period {
  id: number;
  month_name: string;
  month_number: number;
  year: number;
  season: string;
  start_day: number;
  end_day: number;
  month_genitive: string;
  is_active: number;
  created_at: string;
}

const PeriodsManager: React.FC = () => {
  const [periods, setPeriods] = useState<Period[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<Period | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState({
    month_name: '',
    month_number: 1,
    year: new Date().getFullYear(),
    season: 'Лето',
    start_day: 1,
    end_day: 31,
    month_genitive: '',
    is_active: 0,
  });

  const loadPeriods = async () => {
    try {
      setLoading(true);
      const response = await axios.get('http://192.168.50.43:5002/api/periods');
      const data = extractArrayFromResponse<Period>(response.data);
      setPeriods(data);
    } catch (error) {
      console.error('Ошибка загрузки периодов:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки данных', severity: 'error' });
      setPeriods([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPeriods();
  }, []);

  const handleSave = async () => {
    if (!formData.month_name || !formData.year) {
      setSnackbar({ open: true, message: 'Заполните название месяца и год', severity: 'error' });
      return;
    }
    try {
      if (editingPeriod) {
        await axios.put(`http://192.168.50.43:5002/api/periods/${editingPeriod.id}`, formData);
        setSnackbar({ open: true, message: 'Период обновлен', severity: 'success' });
      } else {
        await axios.post('http://192.168.50.43:5002/api/periods', formData);
        setSnackbar({ open: true, message: 'Период создан', severity: 'success' });
      }
      setOpenDialog(false);
      loadPeriods();
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setSnackbar({ open: true, message: 'Ошибка сохранения', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить период?')) return;
    try {
      await axios.delete(`http://192.168.50.43:5002/api/periods/${id}`);
      setSnackbar({ open: true, message: 'Период удален', severity: 'success' });
      loadPeriods();
    } catch (error) {
      console.error('Ошибка удаления:', error);
      setSnackbar({ open: true, message: 'Ошибка удаления', severity: 'error' });
    }
  };

  const handleSetActive = async (id: number) => {
    try {
      await axios.put(`http://192.168.50.43:5002/api/periods/${id}`, { is_active: 1 });
      setSnackbar({ open: true, message: 'Период активирован', severity: 'success' });
      // Сообщаем Layout, что период изменился
      window.dispatchEvent(new Event('periodActivated'));
      loadPeriods();
    } catch (error) {
      console.error('Ошибка активации:', error);
      setSnackbar({ open: true, message: 'Ошибка активации', severity: 'error' });
    }
  };

  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const monthGenitives = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
  ];

  const seasons = ['Зима', 'Весна', 'Лето', 'Осень'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CalendarMonth sx={{ fontSize: 32, color: 'primary.main' }} />
          
          <Chip label={`${periods.length} периодов`} size="small" variant="outlined" />
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingPeriod(null);
            setFormData({
              month_name: '',
              month_number: 1,
              year: new Date().getFullYear(),
              season: 'Лето',
              start_day: 1,
              end_day: 31,
              month_genitive: '',
              is_active: 0,
            });
            setOpenDialog(true);
          }}
        >
          Добавить период
        </Button>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>ID</TableCell>
                <TableCell>Месяц</TableCell>
                <TableCell>Год</TableCell>
                <TableCell>Сезон</TableCell>
                <TableCell>Период</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      Нет периодов. Создайте первый период.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                periods.map((period) => (
                  <TableRow
                    key={period.id}
                    hover
                    sx={{
                      opacity: period.is_active ? 1 : 0.6,
                      bgcolor: period.is_active ? 'action.hover' : 'transparent',
                    }}
                  >
                    <TableCell>{period.id}</TableCell>
                    <TableCell>{period.month_name}</TableCell>
                    <TableCell>{period.year}</TableCell>
                    <TableCell>
                      <Chip
                        label={period.season}
                        size="small"
                        color={
                          period.season === 'Лето' ? 'warning' :
                          period.season === 'Зима' ? 'info' :
                          period.season === 'Весна' ? 'success' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {period.start_day}.{period.month_number}.{period.year} —
                      {period.end_day}.{period.month_number}.{period.year}
                    </TableCell>
                    <TableCell>
                      {period.is_active === 1 ? (
                        <Chip label="Активный" size="small" color="success" icon={<CheckCircle />} />
                      ) : (
                        <Chip label="Неактивный" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {period.is_active !== 1 && (
                        <Tooltip title="Сделать активным">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleSetActive(period.id)}
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Редактировать">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setEditingPeriod(period);
                            setFormData({
                              month_name: period.month_name,
                              month_number: period.month_number,
                              year: period.year,
                              season: period.season,
                              start_day: period.start_day,
                              end_day: period.end_day,
                              month_genitive: period.month_genitive || '',
                              is_active: period.is_active,
                            });
                            setOpenDialog(true);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {period.is_active !== 1 && (
                        <Tooltip title="Удалить">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(period.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Modal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={editingPeriod ? 'Редактирование периода' : 'Новый период'}
        actions={
          <>
            <Button
              onClick={() => setOpenDialog(false)}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {editingPeriod ? 'Обновить' : 'Создать'}
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Название месяца</InputLabel>
            <Select
              value={formData.month_name}
              onChange={(e) => {
                const idx = monthNames.indexOf(e.target.value);
                setFormData({
                  ...formData,
                  month_name: e.target.value,
                  month_number: idx + 1,
                  month_genitive: monthGenitives[idx] || '',
                });
              }}
              label="Название месяца"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                }
              }}
            >
              {monthNames.map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Родительный падеж"
            value={formData.month_genitive}
            onChange={(e) => setFormData({ ...formData, month_genitive: e.target.value })}
            fullWidth
            placeholder="например: января"
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#ffffff',
              }
            }}
          />

          <TextField
            label="Год"
            type="number"
            value={formData.year}
            onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) || new Date().getFullYear() })}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#ffffff',
              }
            }}
          />

          <FormControl fullWidth>
            <InputLabel>Сезон</InputLabel>
            <Select
              value={formData.season}
              onChange={(e) => setFormData({ ...formData, season: e.target.value })}
              label="Сезон"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                }
              }}
            >
              {seasons.map((season) => (
                <MenuItem key={season} value={season}>{season}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Начало дня"
              type="number"
              value={formData.start_day}
              onChange={(e) => setFormData({ ...formData, start_day: parseInt(e.target.value) || 1 })}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                }
              }}
            />
            <TextField
              label="Конец дня"
              type="number"
              value={formData.end_day}
              onChange={(e) => setFormData({ ...formData, end_day: parseInt(e.target.value) || 31 })}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                }
              }}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active === 1}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                color="success"
              />
            }
            label={formData.is_active === 1 ? 'Активный период' : 'Неактивный период'}
            sx={{
              '& .MuiFormControlLabel-label': {
                fontWeight: 500,
                color: formData.is_active === 1 ? '#2e7d32' : '#d32f2f',
              }
            }}
          />
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PeriodsManager;
